import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from '../icons';

export type ButtonVariant = 'default' | 'primary' | 'danger' | 'ghost' | 'outline' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon by name. */
  icon?: IconName;
  /** Trailing icon by name. */
  iconRight?: IconName;
  /** Shows a spinner and disables the button; the label stays for layout stability. */
  busy?: boolean;
  block?: boolean;
  /** Solid danger (destructive confirm) instead of the outlined danger. */
  solid?: boolean;
}

function cx(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(' ');
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', size = 'md', icon, iconRight, busy, block, solid, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('hf-btn', variant !== 'default' && variant, size !== 'md' && size, busy && 'busy', block && 'block', solid && 'solid', className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === 'sm' ? 13 : 15} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === 'sm' ? 13 : 15} />}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children'> {
  icon: IconName;
  /** Required: the accessible name (also used as the tooltip title). */
  label: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, size = 'md', variant = 'ghost', className, ...rest },
  ref,
) {
  return (
    <Button ref={ref} variant={variant} size={size} className={cx('hf-iconbtn', className)} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={size === 'sm' ? 14 : 16} />
    </Button>
  );
});

export function ButtonGroup({ children, 'aria-label': label }: { children: ReactNode; 'aria-label'?: string }) {
  return (
    <div className="hf-btn-group" role="group" aria-label={label}>
      {children}
    </div>
  );
}
